"use client"

import { useEffect, useRef, useState } from "react"
import Flickity from "react-flickity-component"
import { MdFormatQuote } from "react-icons/md"
import adamParry from "../images/adam-parry.png"
import finalist from "../images/news/finalist-eta.png"
import mea_shortlist from "../images/news/mea-shortlist.png"
import { Button } from "../myne/Button/Button"
import useWindowDimensions from "../utils/useWindowDimensions"
import rounded_left from "../images/vectors/rounded-left.png"
import wave_right from "../images/vectors/wave-right.png"

export interface NewsItem {
  id: string | number
  date: string
  title: string
  quote?: string
  authorName?: string
  authorRole?: string
  body?: string
  image?: string
  imageBadge?: boolean
  href?: string
}

interface PremagicNewsSectionProps {
  items?: NewsItem[]
  isHomePage?: boolean
}

const DEFAULT_NEWS: NewsItem[] = [
  {
    id: 1,
    date: "14 Apr 2026",
    title: "Adam Parry of Event Tech Live Joins Premagic as Strategic Advisor",
    body: "What Premagic has created is genuinely useful – it makes events better for attendees and measurably more visible for organisers. I'm proud to be part of this chapter and looking forward to helping connect the product with the people who need it most",
    authorName: "Adam Parry",
    authorRole: "Co-Founder, Event Tech Live",
    image: adamParry,
    imageBadge: false,
    href: "https://blog.premagic.com/adam-parry-joins-premagic-as-strategic-advisor/",
  },
  {
    id: 2,
    date: "15 Apr 2026",
    title:
      "Finalist for Best AI for Sponsorship Activation & Measurement at the Event Technology Awards: AI Edition 2026",
    body: "We are officially a finalist for Best AI for Sponsorship Activation & Measurement for our work at the Middle East Event Show (MEES) where we introduced an entirely new sponsorship category called the AI-Powered Event Gallery Sponsor.",
    image: finalist,
    imageBadge: true,
    href: "https://blog.premagic.com/finalist-premagic-shortlisted-for-the-event-technology-awards-ai-edition-2026/",
  },
  {
    id: 3,
    date: "7 May 2026",
    title:
      "Premagic shortlisted for Best Use of AI at an Event at the Middle East Event Awards 2026",
    body: "We are honored to be shortlisted for Best Use of AI at an Event recognizing our work with AI Everything MEA Egypt, where we used AI avatars to turn attendee advocacy into a registration engine; driving over 2,000 sign-ups in just one week.",
    image: mea_shortlist,
    imageBadge: false,
    href: "https://blog.premagic.com/premagic-shortlisted-middle-east-event-awards-2026/",
  },
]

export default function PremagicNewsSection({
  items = DEFAULT_NEWS,
  isHomePage = false,
}: PremagicNewsSectionProps) {
  const { width } = useWindowDimensions()
  return (
    <section className="relative w-full py-14 sm:py-20 overflow-hidden bg-[#F3EDFF]">
      <div className="pointer-events-none absolute -top-40 right-0 w-[600px] h-[400px] rounded-full bg-violet-100/60 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 -left-20 w-[400px] h-[300px] rounded-full bg-blue-100/50 blur-[100px]" />

      {/* Heading */}
      <h2
        className={`relative text-center text-3xl ${
          isHomePage ? "md:text-5xl" : "md:text-4xl"
        } font-bold text-gray-900 mb-10 sm:mb-14 tracking-tight`}
      >
        Premagic News
      </h2>

      <div className="relative z-10">
        {width < 768 ? (
          <MobileSwiper items={items} />
        ) : (
          <DesktopSwiper items={items} />
        )}
      </div>
      <div className="absolute top-[100px] -left-4 z-4">
        <img src={rounded_left} alt="Vector" width={500} />
      </div>

      <div className="absolute -top-[60px] right-0 z-4">
        <img src={wave_right} alt="Vector" width={500} />
      </div>
    </section>
  )
}

function NewsCard({ item }: { item: NewsItem }) {
  return (
    <div className="flex flex-col !h-full max-w-4xl rounded-3xl bg-gradient-to-r from-[#876DF4] via-[#7253F5] to-[#67AFF6] text-white py-6 px-8 md:p-12 md:px-[65px] transition-all duration-300">
      <p className="text-white text-[11px] font-medium tracking-widest uppercase mb-3">
        {item.date}
      </p>

      <div className="flex items-start flex-col md:flex-row justify-between gap-3 mb-4">
        <h3 className="text-white text-xl md:text-[44px] font-semibold !leading-[1.2em] max-w-full md:max-w-lg">
          {item.title}
        </h3>

        {item.image && (
          <div
            className={`flex-shrink-0 w-[150px] h-[150px] md:w-[230px] md:h-[230px] rounded-3xl md:rounded-[32px] overflow-hidden border border-white mt-4 md:mt-0 bg-[#3c355c]`}
          >
            <img
              src={item.image}
              alt={item.authorName ?? item.title}
              className="w-full h-full object-cover rounded-xl "
            />
          </div>
        )}
      </div>

      {item.body && !item.quote && (
        <div className="flex-1 mb-4">
          <p className="text-white text-base md:text-md leading-relaxed line-clamp-5 font-secondary">
            {item.body}
          </p>
        </div>
      )}

      {item.authorName && (
        <div className="mb-4">
          <span className="text-white text-md font-semibold md:inline block">
            {item.authorName}
          </span>
          {item.authorRole && (
            <span className="text-white text-md md:ml-2 ">
              {item.authorRole}
            </span>
          )}
        </div>
      )}

      {item.href && (
        <Button to={item.href} variant="white" className="mt-4">
          Know More
        </Button>
      )}
    </div>
  )
}

function MobileSwiper({ items }) {
  const flickityRef = useRef<any>(null)

  const handleFlickityRef = (c: any) => {
    flickityRef.current = c
  }

  const flickityOptions = {
    wrapAround: true,
    cellAlign: "center",
    autoPlay: false,
    pageDots: false,
    prevNextButtons: false,
    draggable: true,
    selectedAttraction: 0.025,
    friction: 0.28,
    adaptiveHeight: false,
  }

  return (
    <>
      <div className="w-full overflow-x-auto scroll-smooth scrollbar-hide pb-4">
        <div className="flex items-stretch gap-[10px] min-w-max ml-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="min-w-[300px] max-w-[360px] flex-shrink-0 flex"
            >
              <div className="h-full w-full">
                <NewsCard item={item} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function DesktopSwiper({ items }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [inView, setInView] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])
  return (
    <div ref={containerRef} className="relative">
      <div
        className="marquee-track"
        style={{
          animationPlayState: inView && !isHovered ? "running" : "paused",
        }}
      >
        {[...items, ...items].map((item, idx) => (
          <div
            key={idx}
            className="marquee-item"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <NewsCard item={item} />
          </div>
        ))}
      </div>
    </div>
  )
}
